import { endOfDay, isBefore, startOfToday } from 'date-fns';
import * as Yup from 'yup';

import { MAX_SHOWCASE_DESCRIPTION_LENGTH } from '~/components/ShowcaseModal/consts';

const MIN_PASSCODE_LENGTH = 8;

export const ShortUrlPropsValidationSchema = Yup.object().shape({
  name: Yup.string().required('Showcase name is required'),
  description: Yup.string()
    .nullable()
    .max(MAX_SHOWCASE_DESCRIPTION_LENGTH, `Description must be ${MAX_SHOWCASE_DESCRIPTION_LENGTH} characters or fewer`)
    .default(null),
  disabled: Yup.boolean().required('Disabled is required'),
  passcode: Yup.string()
    .nullable()
    .min(MIN_PASSCODE_LENGTH, 'Passcode must be at least 8 characters.')
    .matches(/\d+/, 'Passcode must include a number.')
    .matches(/[a-z]/, 'Passcode must include one lowercase letter.')
    .matches(/[A-Z]/, 'Passcode must include one uppercase letter.')
    .default(''),
  expirationDate: Yup.string()
    .nullable()
    .test('is-valid-date', 'Expiration date must be a valid date', (value) => {
      if (!value) {
        return true;
      }
      const date = new Date(value);

      return !Number.isNaN(date.getTime());
    })
    .test('is-future-date', 'Expiration date cannot be set in the past', (value) => {
      if (!value) {
        return true;
      }
      const date = new Date(value);

      return !isBefore(endOfDay(date), startOfToday());
    })
    .default(null),
  presentationSettings: Yup.object()
    .shape({
      hideInfoTab: Yup.boolean().optional(),
    })
    .nullable()
    .optional(),
  permissions: Yup.object()
    .shape({
      asset: Yup.object().shape({
        canViewOriginal: Yup.boolean().required('Can view original is required'),
        canViewAllVersions: Yup.boolean().required('Can view all versions is required'),
        canViewDiscussions: Yup.boolean().required('Can view discussions is required'),
        canManageDiscussions: Yup.boolean().required('Can manage discussions is required'),
      }),
      customField: Yup.object().shape({
        canView: Yup.boolean().required('Can view is required'),
        canManageApplications: Yup.boolean().required('Can manage applications is required'),
      }),
    })
    .required('Permissions are required'),
});

export type ValidateShortUrlPropsData = Yup.InferType<typeof ShortUrlPropsValidationSchema>;

/**
 * Validates ShortUrlProps against the validation schema and returns validation results without throwing.
 *
 * @param data - The ShortUrlProps data to validate
 * @returns An object containing:
 *   - isValid: boolean indicating if validation passed
 *   - data: the validated/coerced data if valid, otherwise the original data
 *   - errors: an object mapping field paths to error messages, or null if valid
 *   - errorMessages: an array of all error messages, or null if valid
 */
export async function validateShortUrlProps(data: ValidateShortUrlPropsData): Promise<{
  isValid: boolean;
  data: ValidateShortUrlPropsData;
  errors: Record<string, string> | null;
  errorMessages: string[] | null;
}> {
  try {
    const validatedData = await ShortUrlPropsValidationSchema.validate(data, { abortEarly: false });

    return {
      isValid: true,
      data: validatedData,
      errors: null,
      errorMessages: null,
    };
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      const errorsByPath = error.inner.reduce<Record<string, string>>((acc, err) => {
        if (err.path) {
          acc[err.path] = err.message;
        }

        return acc;
      }, {});

      return {
        isValid: false,
        data,
        errors: Object.keys(errorsByPath).length > 0 ? errorsByPath : null,
        errorMessages: error.errors.length > 0 ? error.errors : null,
      };
    }
    throw error;
  }
}
